
/**
 * VIEWMODEL DE CADASTRO DE CONTAS A PAGAR
 */
import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { processError } from "../../../../common/utils/base_error";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { DropDown } from "../../../../common/domain/models/dropdown";
import { ContasPagar } from "../../domain/models/contas_pagar";
import { ContasPagarForm, ContasPagarSchema, defaultContaPagarForm } from "../schemas/ContasPagar_cadastro/ContasPagar_form";
import { ContasPagarCopiaForm } from "../schemas/ContasPagar_lista/ContasPagar_copia";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { InformacoesBancarias } from "../../domain/models/informacoes_bancarias";
import { FileUtils } from "../../../../common/utils/file_utils";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";
import { useUserAuthStore } from "../../../../stores/global/userAuth";

export const useContasPagarCadastroViewModel = () => {
    const {
        reports: {
            getReportContaPagarUseCase,
        },
        commons: {
            getCentroCustoDropDownUseCase,
            getBancosPagamentoDropDownUseCase,
            getOrdemComprasDropDownUseCase,
            getSubcontasDropDownUseCase,
            getAllLogAlteracaoUseCase,
            getAllLogAlteracaoByParentUseCase,
            getFornecedorDropDownUseCase,
            getFuncionarioByIdUsuarioUseCase,
        },
        financeiro: {
            getInformacoesBancariasFornecedorByIdUseCase,
            getInformacoesBancariasFuncionarioByIdUseCase,
            getContasPagarByIdUseCase,
            deleteContasPagarUseCase,
            createContasPagarUseCase,
            updateContasPagarUseCase,
            cancelarPagamentoUseCase,
            copiaContasPagarUseCase,
            getContasPagarArquivoByIdUseCase,
        },
    } = useDependencyInjection();

    const location = useLocation();
    const navigate = useNavigate();
    const user = useUserAuthStore();

    const menuRef = useRef<HTMLButtonElement>(null);
    const [openMenu, setOpenMenu] = useState(false);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const { pushErrorNotification, pushInfoNotification, pushWarnNotification, setCustomLoading } = usePageLoader();

    const [contaPagarSelectedId, setContaPagarSelectedId] = useState<number | undefined>();
    const [contaPagar, setContaPagar] = useState<ContasPagar>();
    const [statusBloqueio, setStatusBloqueio] = useState<boolean>(false);

    const [informacoesBancariasFornecedor, setInformacoesBancariasFornecedor] = useState<InformacoesBancarias | null>();
    const [informacoesBancariasFuncionario, setInformacoesBancariasFuncionario] = useState<InformacoesBancarias | null>();

    const [variasOrdensCompra, setVariasOrdensCompra] = useState<boolean>(false);

    //MODAIS
    const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
    const [openDeleteArquivoModal, setOpenDeleteArquivoModal] = useState<boolean>(false);

    const [tituloCopia, setTituloCopia] = useState<string | undefined>();
    const [openCopiaModal, setOpenCopiaModal] = useState<boolean>(false);


    //ARQUIVOS
    const [limparArquivo, setLimparArquivo] = useState<string>();
    const [nomeArquivo, setNomeArquivo] = useState<string>();

    //DROPDOWNS
    const [ddlCentroCusto, setDdlCentroCusto] = useState<DropDown[]>();
    const [ddlFornecedor, setDdlFornecedor] = useState<DropDown[]>();
    const [ddlBanco, setDdlBanco] = useState<DropDown[]>();
    const [ddlOrdemCompra, setDdlOrdemCompra] = useState<DropDown[]>();
    const [ddlSubconta, setDdlSubconta] = useState<DropDown[]>();

    const [reportViewerState, startReportViewerProcess] = useReportViewer();

    const changeHistory = useChangeHistory({
        getAllLogAlteracaoUseCase,
        getAllLogAlteracaoByParentUseCase,
        onSetError: (e) => pushErrorNotification("Erro", [e]),
    });

    const hookForm = useForm<ContasPagarForm>({
        reValidateMode: "onSubmit",
        mode: "onSubmit",
        defaultValues: defaultContaPagarForm,
        resolver: zodResolver(ContasPagarSchema),
    });

    const isFornecedor = hookForm.watch("isFornecedor");

    const idFornecedor = hookForm.watch("idFornecedor");
    const idFuncionario = hookForm.watch("idFuncionarioPago");

    const valorMulta = hookForm.watch("valorMulta") ?? 0;
    const valorJuros = hookForm.watch("valorJuros") ?? 0;

    //#region CARREGAMENTO

    const loadInformacaoBancariaFornecedor = useCallback(async (idFornecedor: number) => {
        try {
            const info = await getInformacoesBancariasFornecedorByIdUseCase.getInformacoesBancariasFornecedorById(idFornecedor);
            setInformacoesBancariasFornecedor(info);
            setInformacoesBancariasFuncionario(null);
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao buscar informações bancárias do fornecedor!"));
        }
    }, [
        getInformacoesBancariasFornecedorByIdUseCase,
        pushErrorNotification
    ]);


    const loadInformacaoBancariaFuncionario = useCallback(async (idFuncionario: number) => {
        try {
            const info = await getInformacoesBancariasFuncionarioByIdUseCase.getInformacoesBancariasFuncionarioById(idFuncionario);
            setInformacoesBancariasFuncionario(info);
            setInformacoesBancariasFornecedor(null);
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao buscar informações bancárias do funcionário!"));
        }
    }, [
        getInformacoesBancariasFuncionarioByIdUseCase,
        pushErrorNotification
    ]);

    const loadContaPagarSelecionada = useCallback(async (idContaPagar: number) => {
        try {
            const conta = await getContasPagarByIdUseCase.getContasPagarById(idContaPagar);
            hookForm.reset();
            for (const [key, value] of Object.entries(conta)) {
                hookForm.setValue(key as any, value ?? null);
            }

            if (conta.listaIdOrdemCompra) {
                const idsArray = typeof conta.listaIdOrdemCompra === 'string'
                    ? conta.listaIdOrdemCompra.split(',').map(id => parseInt(id.trim(), 10))
                    : conta.listaIdOrdemCompra;
                setVariasOrdensCompra(idsArray.length > 0)
                hookForm.setValue("listaIdOrdemCompra", idsArray);
            } else {
                hookForm.setValue("listaIdOrdemCompra", []);
                setVariasOrdensCompra(false);
            }

            if (conta) {
                hookForm.setValue('arquivo1', null)
                hookForm.setValue('arquivo2', null)
                hookForm.setValue('arquivo3', null)
            }

            if (conta.idFornecedor) {
                hookForm.setValue("isFornecedor", true);
            } else if (conta.idFuncionarioPago) {
                hookForm.setValue("isFornecedor", false);
            }

            if (conta.status === 0 && (conta.dataPagar !== null || conta.dataPagar !== undefined)) {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                if (new Date(conta.dataPagar!).getTime() < today.getTime()) {
                    hookForm.setValue("status", 3); // Atrasado
                }
            }

            setContaPagar(conta);
            setContaPagarSelectedId(idContaPagar);
            setStatusBloqueio(conta.status === 1);

            if (conta.idFornecedor) {
                await loadInformacaoBancariaFornecedor(conta.idFornecedor);
            } else if (conta.idFuncionarioPago) {
                await loadInformacaoBancariaFuncionario(conta.idFuncionarioPago);
            }

        } catch {
            pushErrorNotification("Erro", "Falha ao buscar registro da conta a pagar!");
        }
    }, [hookForm, getContasPagarByIdUseCase, loadInformacaoBancariaFornecedor, loadInformacaoBancariaFuncionario, pushErrorNotification]);

    const loadCentroCustoDropDown = useCallback(async () => {
        try {
            const result = await getCentroCustoDropDownUseCase.getCentroCustoDropDown();
            setDdlCentroCusto(result);
        } catch (e: unknown) {
            pushErrorNotification("Error", processError(e, "Falha ao buscar centro de custo!"));
        }
    }, [getCentroCustoDropDownUseCase, pushErrorNotification]);

    const loadFornecedorDropDown = useCallback(async () => {
        try {
            const result = await getFornecedorDropDownUseCase.getFornecedorDropDown();
            setDdlFornecedor(result.map((x) => ({
                id: x.idFornecedor,
                title: x.nomeFantasia
            })));

        } catch (e: unknown) {
            pushErrorNotification("Error", processError(e, "Falha ao buscar fornecedores!"));
        }
    }, [getFornecedorDropDownUseCase, pushErrorNotification]);

    const loadBancosPagamentoDropDown = useCallback(async () => {
        try {
            const result = await getBancosPagamentoDropDownUseCase.getBancosPagamentoDropDown();
            setDdlBanco(result);
        } catch (e: unknown) {
            pushErrorNotification("Error", processError(e, "Falha ao buscar bancos!"));
        }
    }, [getBancosPagamentoDropDownUseCase, pushErrorNotification]);

    const loadOrdemCompraDropDown = useCallback(async () => {
        try {
            const result = await getOrdemComprasDropDownUseCase.getOrdemComprasDropDown();
            setDdlOrdemCompra(result);
        } catch (e: unknown) {
            pushErrorNotification("Error", processError(e, "Falha ao buscar ordens de compra!"));
        }
    }, [getOrdemComprasDropDownUseCase, pushErrorNotification]);

    const loadSubcontaDropDown = useCallback(async () => {
        try {
            const result = await getSubcontasDropDownUseCase.getSubcontasDropDown();
            setDdlSubconta(result);
        } catch (e: unknown) {
            pushErrorNotification("Error", processError(e, "Falha ao buscar subcontas!"));
        }
    }, [getSubcontasDropDownUseCase, pushErrorNotification]);

    //#endregion



    //#region  PAGE START / USE EFFECT
    usePageStartBootstrap(async () => {

        await Promise.all([
            loadCentroCustoDropDown(),
            loadFornecedorDropDown(),
            loadBancosPagamentoDropDown(),
            loadOrdemCompraDropDown(),
            loadSubcontaDropDown(),
        ]);

        const usuarioInfo = await getFuncionarioByIdUsuarioUseCase.getFuncionarioByIdUsuario(user.idUsuario);
        requestAnimationFrame(() => {
            if (usuarioInfo !== null && usuarioInfo) {
                hookForm.setValue('idFuncionario', usuarioInfo.idFuncionario);
            }
            setLoading(false);
        });

        const idContaPagar = contaPagarSelectedId ?? (location.state?.idContasPagar as number);
        if (idContaPagar) {
            await loadContaPagarSelecionada(idContaPagar);
        } else {
            setStatusBloqueio(true);
        }
    });


    useEffect(() => {
        const loadInformacaoBancaria = async () => {
            if (idFornecedor) {
                await loadInformacaoBancariaFornecedor(idFornecedor);
                hookForm.setValue("idFuncionarioPago", null);
            } else {
                setInformacoesBancariasFornecedor(null);
            }

            if (idFuncionario) {
                await loadInformacaoBancariaFuncionario(idFuncionario);
                hookForm.setValue("idFornecedor", null);
            } else {
                setInformacoesBancariasFuncionario(null);
            }

            if (valorJuros !== null && valorJuros !== undefined && valorJuros > 0) {
                hookForm.setValue("valorPago", Number(hookForm.getValues("valorPagar")) + Number(valorJuros) + Number(valorMulta));
            }

            if (valorMulta !== null && valorMulta !== undefined && valorMulta > 0) {
                hookForm.setValue("valorPago", Number(hookForm.getValues("valorPagar")) + Number(valorJuros) + Number(valorMulta));
            }
        };


        loadInformacaoBancaria();
    }, [
        hookForm,
        idFornecedor,
        idFuncionario,
        valorJuros,
        valorMulta,
        loadInformacaoBancariaFornecedor,
        loadInformacaoBancariaFuncionario,
    ]);


    //#endregion




    //#region MUDANÇA STATUS 


    const handleStatusChange = useCallback(async (newStatus: number) => {
        try {
            const valorPagar = Number(hookForm.getValues('valorPagar')) || 0;
            const dataPago = new Date();

            hookForm.setValue("status", newStatus)
            hookForm.setValue("valorPago", valorPagar)
            hookForm.setValue("dataPago", dataPago)

            if (contaPagar) {
                setContaPagar(param => param ? ({
                    ...param,
                    status: newStatus,
                    valorBaixa: valorPagar,
                    dataBaixa: dataPago

                }) : undefined);
            }
        } catch (e: unknown) {
            pushErrorNotification("Erro", "Falha ao mudar Status!")
        }
    }, [
        hookForm,
        contaPagar,
        pushErrorNotification
    ]);


    //#endregion






    //#region CABEÇALHO

    const handlePageBack = useCallback(() => {
        navigate('/ContasPagar_lista')
    }, [navigate]);

    const handleNewContaPagar = useCallback(() => {

        hookForm.reset();
        setContaPagar(undefined);
        setContaPagarSelectedId(undefined);
        setStatusBloqueio(true);

    }, [hookForm]);

    const handleCloseMenu = useCallback((event: any) => {
        if (
            menuRef.current &&
            menuRef.current.contains(event.target as HTMLElement)
        ) {
            return;
        }
        setOpenMenu(false);
    }, []);

    const handleToggleMenu = useCallback(() => {
        setOpenMenu((prev) => !prev);
    }, []);

    const handleListKeyDown = useCallback((event: React.KeyboardEvent) => {
        if (event.key === 'Tab') {
            event.preventDefault();
            setOpenMenu(false);
        } else if (event.key === 'Escape') {
            setOpenMenu(false);
        }
    }, []);

    //#endregion





    //#region SALVAR/UPDATE
    const handleSalvarContaPagar = useCallback(async (form: ContasPagarForm) => {
        try {
            setSaving(true);
            setLoading(true);
            setCustomLoading(true, "Enviando dados...");
            if (form.arquivo1 !== null) FileUtils.validateFileSize(form.arquivo1);
            if (form.arquivo2 !== null) FileUtils.validateFileSize(form.arquivo2);
            if (form.arquivo3 !== null) FileUtils.validateFileSize(form.arquivo3);

            const contaData = {
                ...form,
                idFornecedor: form.isFornecedor ? form.idFornecedor : undefined,
                idFuncionarioPago: form.isFornecedor ? undefined : form.idFuncionarioPago,
                dataPagar: form.dataPagar ? new Date(form.dataPagar) : undefined,
                dataPago: form.dataPago ? new Date(form.dataPago) : undefined,
                arquivo1: form.arquivo1 ? form.arquivo1 : undefined,
                arquivo2: form.arquivo2 ? form.arquivo2 : undefined,
                arquivo3: form.arquivo3 ? form.arquivo3 : undefined,
                status: form.status === 3 ? 0 : form.status
            };

            if (form.valorPagar && form.valorPagar <= 0) {
                pushWarnNotification("Atenção", "O valor a pagar deve ser positivo!");
                setSaving(false);
                setLoading(false);
                return;
            }

            let idContaPagar: number;
            if (contaPagarSelectedId) {
                idContaPagar = await updateContasPagarUseCase.updateContasPagar({
                    ...contaData,
                    listaIdOrdemCompra: form.listaIdOrdemCompra ? form.listaIdOrdemCompra.join(",") : undefined,
                    idContasPagar: contaPagarSelectedId
                });
                pushInfoNotification("Sucesso", "Conta a Pagar atualizada com sucesso!");
            } else {
                idContaPagar = await createContasPagarUseCase.createContasPagar({
                    ...contaData,
                    listaIdOrdemCompra: form.listaIdOrdemCompra ? form.listaIdOrdemCompra.join(",") : undefined,
                });
                pushInfoNotification("Sucesso", "Conta a Pagar criada com sucesso!");
            }

            await loadContaPagarSelecionada(idContaPagar);
            setContaPagarSelectedId(idContaPagar);

        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao salvar conta a pagar!"));
        } finally {
            setSaving(false);
            setLoading(false);
            setCustomLoading(false);
        }
    }, [
        createContasPagarUseCase,
        updateContasPagarUseCase,
        contaPagarSelectedId,
        loadContaPagarSelecionada,
        setCustomLoading,
        pushInfoNotification,
        pushErrorNotification,
        pushWarnNotification,
    ]);


    //#endregion







    //#region EXCLUSÃO
    const handleContaPagarDeleteOpenState = useCallback((idContasPagar: number | null, openState: boolean) => {
        if (openState && idContasPagar) {
            setOpenDeleteModal(openState)
        } else {
            setOpenDeleteModal(openState)
        }
    }, []);

    const handleConfirmarDeletarContaPagar = useCallback(async () => {
        try {
            if (contaPagarSelectedId) {
                await deleteContasPagarUseCase.deleteContasPagarByIds([contaPagarSelectedId], "cadastro");
                pushInfoNotification("Sucesso", "Conta a pagar deletada com sucesso!");
                navigate("/ContasPagar_lista");
            }
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao deletar conta a pagar!"));
        }
    }, [contaPagarSelectedId,
        deleteContasPagarUseCase,
        pushInfoNotification,
        pushErrorNotification,
        navigate]);


    //#endregion





    //#region CANCELAR PAGAMENTO

    const handleCancelarPagamento = useCallback(async () => {
        try {
            if (contaPagar && contaPagar.idContasPagar) {
                await cancelarPagamentoUseCase.cancelarPagamento(contaPagar.idContasPagar);
                await loadContaPagarSelecionada(contaPagar.idContasPagar);
                pushInfoNotification("Sucesso", "Pagamento cancelado com sucesso!");
            }
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao cancelar pagamento!"));
        }
    }, [contaPagar,
        cancelarPagamentoUseCase,
        loadContaPagarSelecionada,
        pushErrorNotification,
        pushInfoNotification]);


    //#endregion




    //#region COPIA

    const handleContaPagarCopiaOpenState = useCallback((openState: boolean) => {
        if (openState && contaPagar) {
            setTituloCopia(contaPagar.nome ?? '');
            setOpenCopiaModal(true);

        } else {
            setOpenCopiaModal(false);
            setTituloCopia('');
        }
    }, [contaPagar]);

    const handleSubmitCopiaContaPagar = useCallback(async (form: ContasPagarCopiaForm) => {
        try {
            if (contaPagarSelectedId) {
                setSaving(true);
                await copiaContasPagarUseCase.copiaContasPagar(form)
                pushInfoNotification("Sucesso", "Conta a Pagar copiada com sucesso!", { isAutoFinish: true });

            } else {
                pushWarnNotification("Atenção", "Nenhuma conta a Pagar selecionada para cópia!");
                return;
            }
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao copiar Conta a Pagar!"));
        } finally {
            setOpenCopiaModal(false);
            setTituloCopia('');
            setSaving(false);
        }
    }, [
        contaPagarSelectedId,
        copiaContasPagarUseCase,
        pushInfoNotification,
        pushErrorNotification,
        pushWarnNotification,
    ]);

    //#endregion





    //#region ARQUIVOS

    const handleDownload = useCallback(async (nroArquivo: number, imageName: string) => {
        if (!saving && contaPagarSelectedId) {
            try {
                setCustomLoading(true, "Preparando download...");
                await getContasPagarArquivoByIdUseCase.getContasPagarArquivoById(contaPagarSelectedId, nroArquivo, imageName);
            } catch (e: unknown) {
                pushErrorNotification("Erro", processError(e, 'Falha ao fazer download do arquivo!'));
            } finally {
                setCustomLoading(false);
            }
        };
    }, [
        saving,
        contaPagarSelectedId,
        getContasPagarArquivoByIdUseCase,
        setCustomLoading,
        pushErrorNotification,
    ]);

    const handleArquivoDeleteOpenState = useCallback((limparArquivos: string | null, nomeArquivo: string | null, openState: boolean) => {
        setOpenDeleteArquivoModal(openState)
        if (openState && limparArquivos && nomeArquivo) {
            setLimparArquivo(limparArquivos)
            setNomeArquivo(nomeArquivo)
        } else {
            setLimparArquivo(undefined)
            setNomeArquivo(undefined)
        }

    }, []);

    const handleDeleteFile = useCallback((limparArquivos: string, nomeArquivo: string) => {
        if (!saving) {
            hookForm.setValue(limparArquivos as any, true);
            hookForm.setValue(nomeArquivo as any, null);
            setContaPagar(prev => prev ? ({ ...prev, [nomeArquivo]: null }) : undefined)
        }
    }, [
        saving,
        hookForm,
    ]);

    const handleConfirmarDeletarArquivo = useCallback(async () => {
        try {
            if (limparArquivo && nomeArquivo) {
                handleDeleteFile(limparArquivo, nomeArquivo);
            }
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, "Falha ao deletar arquivo!"), { isAutoFinish: true });
        } finally {
            setOpenDeleteArquivoModal(false)
            setNomeArquivo(undefined)
            setLimparArquivo(undefined)
        }

    }, [limparArquivo,
        nomeArquivo,
        pushErrorNotification,
        handleDeleteFile]);


    const handleFileChange = useCallback((nomeArquivo: string, limparArquivos: string, e: ChangeEvent<HTMLInputElement>, onChange: (e: any) => void) => {
        if (!saving) {
            hookForm.setValue(limparArquivos as any, false);
            hookForm.setValue(nomeArquivo as any, e.target.files?.item(0)?.name ?? '');
            onChange(e.target.files?.item(0));
        }
    }, [
        saving,
        hookForm,
    ]);

    //#endregion





    //#region RATEIO GERAL
    const handleNewRateioGeral = useCallback(() => {

        if (contaPagarSelectedId) {
            navigate('/RateioGeral_cadastro', {
                state: {
                    idContasPagar: contaPagarSelectedId,
                    conta: true,
                }
            });
        }
    }, [navigate, contaPagarSelectedId]);

    //#endregion    


    const handleGenerateReport = useCallback(async () => {
        if (contaPagarSelectedId) {
            try {
                setLoading(true);
                await startReportViewerProcess({
                    fetchCallback: async (mimeType) => {
                        return await getReportContaPagarUseCase.execute(contaPagarSelectedId, mimeType);
                    }
                });

            } catch (e: unknown) {
                pushErrorNotification("Erro", processError(e, 'Falha ao gerar relatório de Conta a pagar!'));
            } finally {
                setLoading(false);
            }
        }
    }, [
        contaPagarSelectedId,
        getReportContaPagarUseCase,
        pushErrorNotification,
        startReportViewerProcess
    ]);



    //#region HISTORICO
    const handleOpenChangeHistory = useCallback((
        idTabela: number | null,
        tipoTabela: LogTipoTabela | null,
    ) => {
        if (idTabela && tipoTabela) {
            changeHistory.handleOpenChangeHistory(idTabela, tipoTabela, false);
        } else {
            if (true) {
                changeHistory.handleOpenChangeHistory(contaPagar?.idContasPagar ?? 0, LogTipoTabela.CONTAS_PAGAR, true);
            }
        }
    }, [
        contaPagar,
        changeHistory,
    ]);

    //#endregion


    const handleMultiOrdemComprasChange = useCallback((value: boolean) => {
        setVariasOrdensCompra(value);
    }, [setVariasOrdensCompra]);

    return {
        hookForm,
        saving,
        loading,
        contaPagar,
        ddlCentroCusto,
        ddlFornecedor,
        ddlBanco,
        ddlOrdemCompra,
        ddlSubconta,
        contaPagarSelectedId,
        statusBloqueio,
        openDeleteModal,
        openDeleteArquivoModal,
        changeHistory,
        openMenu,
        menuRef,
        tituloCopia,
        openCopiaModal,
        informacoesBancariasFornecedor,
        informacoesBancariasFuncionario,
        isFornecedor,
        reportViewerState,
        variasOrdensCompra,
        userId: user?.idUsuario,

        handleMultiOrdemComprasChange,
        handleNewRateioGeral,
        handleGenerateReport,
        handleOpenChangeHistory,
        handleFileChange,
        handleConfirmarDeletarContaPagar,
        handleContaPagarDeleteOpenState,
        handleStatusChange,
        handleSalvarContaPagar: hookForm.handleSubmit(handleSalvarContaPagar),
        handleCancelarPagamento,
        handleDownload,
        handleArquivoDeleteOpenState,
        handleConfirmarDeletarArquivo,
        handlePageBack,
        handleCloseMenu,
        handleListKeyDown,
        handleToggleMenu,
        handleNewContaPagar,
        handleContaPagarCopiaOpenState,
        handleSubmitCopiaContaPagar,

    };
};



